import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { SeasonalProduct } from '@/mocks/seasonalProducts';
import ImageLightbox from '@/components/base/ImageLightbox';
import FadeInSection from '@/components/base/FadeInSection';

interface FeaturedProductCardProps {
  product: SeasonalProduct;
  variant: 'spotlight' | 'banner';
  onEnquire: (productName: string) => void;
}

function ImageCarousel({
  images,
  alt,
  onImageClick,
}: {
  images: string[];
  alt: string;
  onImageClick: (index: number) => void;
}) {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning || index === current) return;
      setIsTransitioning(true);
      setCurrent(index);
      setTimeout(() => setIsTransitioning(false), 500);
    },
    [current, isTransitioning],
  );

  const goNext = useCallback(() => {
    goTo((current + 1) % images.length);
  }, [current, images.length, goTo]);

  const goPrev = useCallback(() => {
    goTo((current - 1 + images.length) % images.length);
  }, [current, images.length, goTo]);

  useEffect(() => {
    intervalRef.current = setInterval(goNext, 4500);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [goNext]);

  const handleManualNext = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    goNext();
  };

  const handleManualPrev = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    goPrev();
  };

  return (
    <div className="relative w-full h-full group/carousel">
      {/* Image Container */}
      <div
        className="relative w-full h-full min-h-[360px] lg:min-h-[480px] overflow-hidden rounded-2xl cursor-pointer"
        onClick={() => onImageClick(current)}
      >
        {images.map((img, idx) => (
          <img
            key={idx}
            src={img}
            alt={`${alt} - Image ${idx + 1}`}
            title={`${alt} - View ${idx + 1}`}
            className={`absolute inset-0 w-full h-full object-cover object-top transition-all duration-500 ease-in-out ${
              idx === current ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
            }`}
            draggable={false}
          />
        ))}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none"></div>

        {/* Hover Glow Effect */}
        <div className="absolute inset-0 opacity-0 group-hover/carousel:opacity-100 transition-opacity duration-500 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-tr from-primary-500/5 via-transparent to-accent-500/10"></div>
        </div>

        {/* Zoom Icon on Hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity duration-300 pointer-events-none">
          <div className="w-14 h-14 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
            <i className="ri-zoom-in-line text-background-50 text-xl"></i>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleManualPrev();
            }}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-background-50/80 hover:bg-background-50 text-foreground-800 opacity-0 group-hover/carousel:opacity-100 transition-all duration-300 shadow-sm z-10 whitespace-nowrap cursor-pointer"
            aria-label="Previous image"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleManualNext();
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-background-50/80 hover:bg-background-50 text-foreground-800 opacity-0 group-hover/carousel:opacity-100 transition-all duration-300 shadow-sm z-10 whitespace-nowrap cursor-pointer"
            aria-label="Next image"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </>
      )}

      {/* Dots */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                if (intervalRef.current) clearInterval(intervalRef.current);
                goTo(idx);
              }}
              className={`rounded-full transition-all duration-300 cursor-pointer ${
                idx === current
                  ? 'w-7 h-2 bg-background-50 shadow-sm'
                  : 'w-2 h-2 bg-background-50/50 hover:bg-background-50/80'
              }`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function FeaturedProductCard({ product, variant, onEnquire }: FeaturedProductCardProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleLightboxPrev = () => {
    setLightboxIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
  };

  const handleLightboxNext = () => {
    setLightboxIndex((prev) => (prev + 1) % product.images.length);
  };

  // === SPOTLIGHT VARIANT (Home Page) ===
  if (variant === 'spotlight') {
    return (
      <>
        <div className="relative bg-background-50 border border-background-200 rounded-2xl overflow-hidden transition-all duration-500 hover:border-primary-200 group/spotlight">
          {/* Subtle Glow */}
          <div className="absolute -inset-1 bg-gradient-to-r from-primary-400/5 via-accent-400/5 to-primary-400/5 rounded-2xl blur-xl opacity-0 group-hover/spotlight:opacity-100 transition-opacity duration-700 pointer-events-none"></div>

          {/* Featured Badge */}
          <div className="absolute top-5 left-5 z-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500/90 to-yellow-500/90 text-background-50 rounded-full text-xs font-semibold shadow-sm">
              <i className="ri-vip-crown-fill text-sm"></i>
              Featured Seasonal Product
            </div>
          </div>

          <div className="flex flex-col lg:flex-row relative">
            {/* Left - Image Carousel */}
            <div className="w-full lg:w-[520px] flex-shrink-0">
              <ImageCarousel
                images={product.images}
                alt={`${product.displayName} - Bassi Enterprises Seasonal Produce`}
                onImageClick={openLightbox}
              />
            </div>

            {/* Right - Content */}
            <div className="flex-1 p-6 md:p-8 lg:p-10 flex flex-col justify-center">
              {/* Product Label */}
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-accent-100 text-accent-800 text-xs font-semibold rounded-full mb-3 w-fit">
                <i className="ri-sun-line text-sm"></i>
                {product.badge}
              </span>

              {/* Heading */}
              <h3 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-3">
                {product.displayName} Supply
              </h3>

              {/* Description */}
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6 max-w-xl">
                {product.description}
              </p>

              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-7">
                {product.badges.map((badge) => (
                  <div
                    key={badge}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 border border-primary-200 rounded-lg text-xs font-medium text-primary-700"
                  >
                    <i className="ri-check-line text-primary-500 text-sm"></i>
                    {badge}
                  </div>
                ))}
              </div>

              {/* Supply Details Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
                <div className="group/detail p-3 rounded-xl bg-background-100 border border-background-200 transition-all duration-300 hover:scale-[1.04] hover:border-primary-300 hover:bg-background-50 cursor-pointer">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-7 h-7 rounded-lg bg-accent-100 flex items-center justify-center transition-transform duration-300 group-hover/detail:scale-110">
                      <i className="ri-calendar-check-line text-accent-600 text-sm"></i>
                    </div>
                    <span className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider">Availability</span>
                  </div>
                  <p className="text-sm font-semibold text-foreground-900">{product.supplyDetails.availability}</p>
                </div>
                <div className="group/detail p-3 rounded-xl bg-background-100 border border-background-200 transition-all duration-300 hover:scale-[1.04] hover:border-primary-300 hover:bg-background-50 cursor-pointer">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center transition-transform duration-300 group-hover/detail:scale-110">
                      <i className="ri-truck-line text-primary-600 text-sm"></i>
                    </div>
                    <span className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider">Supply Type</span>
                  </div>
                  <p className="text-sm font-semibold text-foreground-900">{product.supplyDetails.supplyType}</p>
                </div>
                <div className="group/detail p-3 rounded-xl bg-background-100 border border-background-200 transition-all duration-300 hover:scale-[1.04] hover:border-primary-300 hover:bg-background-50 cursor-pointer">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-7 h-7 rounded-lg bg-secondary-100 flex items-center justify-center transition-transform duration-300 group-hover/detail:scale-110">
                      <i className="ri-building-2-line text-secondary-600 text-sm"></i>
                    </div>
                    <span className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider">Suitable For</span>
                  </div>
                  <p className="text-sm font-semibold text-foreground-900">{product.supplyDetails.suitableFor.join(' | ')}</p>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => onEnquire(product.name)}
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer w-fit shadow-sm hover:shadow-md"
                >
                  {product.buttonText}
                  <i className="ri-arrow-right-line text-lg"></i>
                </button>
                <Link
                  to="/mangoes"
                  className="inline-flex items-center gap-2 px-7 py-3.5 border border-primary-300 text-primary-700 hover:bg-primary-50 text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer w-fit"
                >
                  View Varieties
                  <i className="ri-arrow-right-line text-lg"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Lightbox */}
        {lightboxOpen && (
          <ImageLightbox
            images={product.images}
            currentIndex={lightboxIndex}
            alt={`${product.displayName} - Bassi Enterprises`}
            onClose={() => setLightboxOpen(false)}
            onPrev={handleLightboxPrev}
            onNext={handleLightboxNext}
          />
        )}
      </>
    );
  }

  // === BANNER VARIANT (Products Page) ===
  return (
    <>
      <div className="relative bg-background-50 border border-background-200 rounded-2xl overflow-hidden transition-all duration-500 hover:border-primary-200 group/banner">
        {/* Subtle Glow */}
        <div className="absolute -inset-1 bg-gradient-to-r from-primary-400/5 via-accent-400/5 to-primary-400/5 rounded-2xl blur-xl opacity-0 group-hover/banner:opacity-100 transition-opacity duration-700 pointer-events-none"></div>

        {/* Featured Badge */}
        <div className="absolute top-4 left-4 z-20">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-500/90 to-yellow-500/90 text-background-50 rounded-full text-[10px] font-semibold shadow-sm">
            <i className="ri-vip-crown-fill text-xs"></i>
            Featured
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-stretch relative">
          {/* Image Carousel */}
          <div className="w-full md:w-[380px] flex-shrink-0">
            <div className="relative w-full min-h-[280px] md:min-h-[320px] cursor-pointer" onClick={() => openLightbox(0)}>
              {product.images.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`${product.displayName} - Image ${idx + 1}`}
                  title={`${product.displayName} - Click to view larger`}
                  className={`absolute inset-0 w-full h-full object-cover object-top transition-all duration-500 ease-in-out rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none ${
                    idx === 0 ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
                  }`}
                  draggable={false}
                />
              ))}

              {/* Multi-image indicator */}
              {product.images.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
                  {product.images.map((_, idx) => (
                    <span
                      key={idx}
                      className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                        idx === 0 ? 'bg-background-50 w-5' : 'bg-background-50/50'
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* Hover zoom icon */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/banner:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="w-12 h-12 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
                  <i className="ri-zoom-in-line text-background-50 text-lg"></i>
                </div>
              </div>

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent pointer-events-none"></div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-5 md:p-7 flex flex-col justify-center">
            <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
              <div>
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-accent-100 text-accent-800 text-[10px] font-semibold rounded-full mb-2">
                  <i className="ri-star-fill text-[10px]"></i>
                  Featured Produce Spotlight
                </span>
                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  {product.displayName}
                </h3>
                <p className="text-foreground-500 text-sm font-medium mt-0.5">
                  {product.badge}
                </p>
              </div>
              <button
                onClick={() => onEnquire(product.name)}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer flex-shrink-0 shadow-sm hover:shadow-md"
              >
                Enquire Now
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            <p className="text-foreground-600 text-sm leading-relaxed mb-4 max-w-xl">
              {product.description}
            </p>

            <div className="flex flex-wrap gap-1.5 mb-4">
              {product.badges.map((badge) => (
                <span
                  key={badge}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-50 border border-primary-200 rounded-md text-[10px] font-medium text-primary-700"
                >
                  <i className="ri-check-line text-primary-500 text-[10px]"></i>
                  {badge}
                </span>
              ))}
            </div>

            {/* Compact Supply Details */}
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-1.5 text-xs text-foreground-500 px-2.5 py-1.5 rounded-lg bg-background-100 border border-transparent transition-all duration-300 hover:scale-[1.05] hover:border-primary-200 hover:bg-background-50 cursor-pointer">
                <i className="ri-calendar-check-line text-accent-500 text-sm transition-transform duration-300 group-hover/banner:scale-110"></i>
                <span className="font-medium text-foreground-700">{product.supplyDetails.availability}</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-foreground-500 px-2.5 py-1.5 rounded-lg bg-background-100 border border-transparent transition-all duration-300 hover:scale-[1.05] hover:border-primary-200 hover:bg-background-50 cursor-pointer">
                <i className="ri-truck-line text-primary-500 text-sm transition-transform duration-300 group-hover/banner:scale-110"></i>
                <span className="font-medium text-foreground-700">{product.supplyDetails.supplyType}</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-foreground-500 px-2.5 py-1.5 rounded-lg bg-background-100 border border-transparent transition-all duration-300 hover:scale-[1.05] hover:border-primary-200 hover:bg-background-50 cursor-pointer">
                <i className="ri-building-2-line text-secondary-500 text-sm transition-transform duration-300 group-hover/banner:scale-110"></i>
                <span className="font-medium text-foreground-700">{product.supplyDetails.suitableFor.join(' | ')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <ImageLightbox
          images={product.images}
          currentIndex={lightboxIndex}
          alt={`${product.displayName} - Bassi Enterprises`}
          onClose={() => setLightboxOpen(false)}
          onPrev={handleLightboxPrev}
          onNext={handleLightboxNext}
        />
      )}
    </>
  );
}